import React, { useState, useEffect } from 'react';
import { TENOR_CONFIG } from '../../utils/gifUtils';

interface GifPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectGif: (gifUrl: string) => void;
}

interface TenorGif {
  id: string;
  title: string;
  media_formats: {
    gif: {
      url: string;
      dims: number[];
      size: number;
    };
    tinygif: {
      url: string;
      dims: number[];
      size: number;
    };
  };
}

interface TenorResponse {
  results: TenorGif[];
}

const GifPicker: React.FC<GifPickerProps> = ({ isOpen, onClose, onSelectGif }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [gifs, setGifs] = useState<TenorGif[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Empêcher le défilement du body quand le modal est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Charger les GIFs tendances au démarrage
  useEffect(() => {
    if (isOpen && gifs.length === 0) {
      loadTrendingGifs();
    }
  }, [isOpen]);

  const loadTrendingGifs = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(
        `${TENOR_CONFIG.BASE_URL}/featured?key=${TENOR_CONFIG.API_KEY}&client_key=${TENOR_CONFIG.CLIENT_KEY}&limit=${TENOR_CONFIG.DEFAULT_LIMIT}&contentfilter=${TENOR_CONFIG.CONTENT_FILTER}`
      );
      
      if (!response.ok) {
        throw new Error('Erreur lors du chargement des GIFs');
      }
      
      const data: TenorResponse = await response.json();
      setGifs(data.results);
    } catch (error) {
      console.error('Erreur Tenor API:', error);
      setError('Impossible de charger les GIFs');
    } finally {
      setIsLoading(false);
    }
  };

  const searchGifs = async (query: string) => {
    if (!query.trim()) {
      loadTrendingGifs();
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(
        `${TENOR_CONFIG.BASE_URL}/search?key=${TENOR_CONFIG.API_KEY}&client_key=${TENOR_CONFIG.CLIENT_KEY}&q=${encodeURIComponent(query)}&limit=${TENOR_CONFIG.DEFAULT_LIMIT}&contentfilter=${TENOR_CONFIG.CONTENT_FILTER}`
      );
      
      if (!response.ok) {
        throw new Error('Erreur lors de la recherche');
      }
      
      const data: TenorResponse = await response.json();
      setGifs(data.results);
    } catch (error) {
      console.error('Erreur Tenor API:', error);
      setError('Erreur lors de la recherche');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    searchGifs(searchTerm);
  };

  const handleGifSelect = (gif: TenorGif) => {
    // Utiliser le format tinygif pour un chargement plus rapide
    const gifUrl = gif.media_formats.tinygif?.url || gif.media_formats.gif?.url;
    if (gifUrl) {
      onSelectGif(gifUrl);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="cosmic-card max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-emerald-800/30">
          <h3 className="text-lg font-semibold text-emerald-100">Choisir un GIF</h3>
          <button
            onClick={onClose}
            className="text-emerald-300/80 hover:text-emerald-100 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-emerald-800/30">
          <form onSubmit={handleSearch} className="flex space-x-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un GIF..."
              className="flex-1 bg-gray-800/70 border border-emerald-800/50 rounded-lg px-3 py-2 text-emerald-100 placeholder-emerald-300/60 focus:outline-none focus:ring-2 focus:ring-cyan-400/70"
            />
            <button
              type="submit"
              className="cosmic-button px-4 py-2"
              disabled={isLoading}
            >
              {isLoading ? '🔍' : 'Rechercher'}
            </button>
          </form>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="text-red-400 text-center py-4">
              {error}
            </div>
          )}

          {isLoading && (
            <div className="text-center text-emerald-300/60 py-8">
              <div className="animate-spin w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full mx-auto mb-2"></div>
              Chargement des GIFs...
            </div>
          )}

          {!isLoading && !error && (
            <>
              {gifs.length === 0 ? (
                <div className="text-center text-emerald-300/60 py-8">
                  Aucun GIF trouvé
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {gifs.map((gif) => (
                    <div
                      key={gif.id}
                      className="relative cursor-pointer group rounded-lg overflow-hidden hover:scale-105 transition-transform"
                      onClick={() => handleGifSelect(gif)}
                    >
                      <img
                        src={gif.media_formats.tinygif?.url || gif.media_formats.gif?.url}
                        alt={gif.title}
                        className="w-full h-32 object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 text-white text-sm font-medium bg-black bg-opacity-50 px-2 py-1 rounded">
                          Sélectionner
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-emerald-800/30">
          <div className="flex items-center justify-center text-xs text-emerald-300/60">
            <img 
              src="https://tenor.com/assets/img/tenor-logo.svg" 
              alt="Tenor" 
              className="h-4 mr-2 opacity-60"
            />
            Propulsé par Tenor
          </div>
        </div>
      </div>
    </div>
  );
};

export default GifPicker;
